import React, { useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";

export default function Vehiclespares({ complaintsLobPplPlStateFilterData }) {
  // Normalize data from key + doc_count
  const data = useMemo(() => {
    if (!Array.isArray(complaintsLobPplPlStateFilterData)) return [];
    return complaintsLobPplPlStateFilterData.map((item) => ({
      ...item.key,
      complaintCount: item.doc_count ?? 0,
    }));
  }, [complaintsLobPplPlStateFilterData]);

  // Dynamically extract all keys
  const allKeys = useMemo(() => {
    const keySet = new Set();
    data.forEach((item) => {
      Object.keys(item).forEach((key) => keySet.add(key));
    });
    return Array.from(keySet);
  }, [data]);

  // Build columns from all keys
  const columns = useMemo(
    () =>
      allKeys.map((key) => ({
        accessorKey: key,
        header: key === "complaintCount" ? "Complaint Count" : key,
        cell: (info) => info.getValue() ?? "-",
      })),
    [allKeys]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const rowHeight = 40;
  const maxVisibleRows = 7;
  const scrollNeeded = data.length > maxVisibleRows;
  const maxTableBodyHeight = scrollNeeded
    ? `${rowHeight * maxVisibleRows}px`
    : "auto";

  return (
    <div className="border rounded overflow-hidden w-full">
      {/* Table Header */}
      <table className="w-full table-fixed border border-gray-300 text-center">
        <thead className="bg-gray-200 text-xs sticky top-0 z-10">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="border border-gray-300 px-2 py-1 break-words"
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
      </table>

      {/* Table Body */}
      <div
        className="overflow-y-auto"
        style={{ maxHeight: maxTableBodyHeight }}
      >
        <table className="w-full table-fixed border-t-0 border border-gray-300 text-center">
          <tbody className="font-medium text-sm">
            {data.length > 0 ? (
              table.getRowModel().rows.map((row, idx) => (
                <tr
                  key={row.id}
                  className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                >
                  {row.getVisibleCells().map((cell) => {
                    const value = cell.getValue()?.toString() ?? "";
                    return (
                      <td
                        key={cell.id}
                        className="border px-2 py-1 break-words"
                        title={value}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="border px-2 py-4 text-center font-medium text-gray-500"
                >
                  No complaints data available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
